<script setup lang="ts">
import { Ref, ref, watch, computed } from 'vue'
import GbButton from '@/components/GbButton.vue'
import GbDialog from '@/components/GbDialog.vue'
import { storeToRefs } from 'pinia'
import { useLibraryStore } from './library.store'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import { useUserStore } from '@/authentication/user.store'
import { getCollectionUsers, grantContributors, transferOwnership } from './api'
import ProgressSpinner from 'primevue/progressspinner'
import { ToastProvider, ToastRoot, ToastViewport } from 'reka-ui'
import { ContributorUserInfo } from './library_types'
import UserAutoComplete from './UserAutoComplete.vue'
import RemoveContributorModal from './RemoveContributorModal.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { useLibraryFolderAnalytics } from '@/library/composables/useLibraryFolderAnalytics.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { useVisibilityStore } from '@/visibility/visibility.store'
import PopupMenu from '@/components/PopupMenu.vue'
import type { MenuItemType } from '@/components/dropdown-menu/types'


const props = defineProps({
  externalOpen: {
    type: Boolean,
    default: false
  },
})

const emit = defineEmits(['update:externalOpen', 'open-share'])
const userStore = useUserStore()
const libraryStore = useLibraryStore()
const { currentFolder, hasAddRemoveContributorPerms, doesUserOwnCurrentFolder } = storeToRefs(libraryStore)
const { shareVisibility } = storeToRefs(useVisibilityStore())
const { userId } = storeToRefs(userStore)
const showToast = ref(false)
const toastMessage = ref('')

// Treat ownership as sufficient to add contributors, even if permissions array is briefly stale
const canAddContributors = computed(() => hasAddRemoveContributorPerms.value || doesUserOwnCurrentFolder.value)
const showViewOnlyLinkButton = computed(() => shareVisibility.value && doesUserOwnCurrentFolder.value)

const visibleRef: Ref<boolean> = ref(props.externalOpen)
const updateError: Ref<boolean> = ref(false)
const updatingFolder: Ref<boolean> = ref(false)
const existingUsers: Ref<{user_uuid: string, role: string, user_name: string, user_email: string}[]> = ref([])
const loadingExistingUsers: Ref<boolean> = ref(false)
const errorExistingUsers: Ref<boolean> = ref(false)
const selectedUsers: Ref<ContributorUserInfo[]> = ref([])
const messageToNewContributors: Ref<string> = ref('')
const toastError: Ref<boolean> = ref(false)
const confirmTransferOpen: Ref<boolean> = ref(false)
const confirmTransferTargetUuid: Ref<string> = ref("")
const confirmTransferTargetName: Ref<string> = ref("")
const confirmTransferLoading: Ref<boolean> = ref(false)
const roleChangeError: Ref<boolean> = ref(false)

const openConfirmTransfer = (userUuid: string, userName: string) => {
  confirmTransferTargetUuid.value = userUuid
  confirmTransferTargetName.value = userName
  confirmTransferOpen.value = true
}

const cancelConfirmTransfer = () => {
  confirmTransferOpen.value = false
  confirmTransferTargetUuid.value = ""
  confirmTransferTargetName.value = ""
  confirmTransferLoading.value = false
}

const confirmTransfer = async () => {
  if (!confirmTransferTargetUuid.value || confirmTransferLoading.value) return
  confirmTransferLoading.value = true
  roleChangeError.value = false
  try {
    await handleTransferOwnership(confirmTransferTargetUuid.value)
    await libraryStore.fetchResourcesForFolder({ folderUUID: currentFolder.value?.uuid, params: {} })
    cancelConfirmTransfer()
  }
  catch(err) {
    console.error("Error with transferring ownership", err)
    roleChangeError.value = true
  } finally {
    confirmTransferLoading.value = false
  }
}

// Watch for external open/close
watch(
  () => props.externalOpen,
  (newValue) => {
    visibleRef.value = newValue
    if (newValue) {
      fetchCollectionUsers()
    }
  },
  { immediate: true }
)

// Watch for internal changes and emit
watch(
  () => visibleRef.value,
  (newValue) => {
    if (newValue !== props.externalOpen) {
      emit('update:externalOpen', newValue)
    }
  }
)

const displayRole = function (role: string): string {
  return role[0].toUpperCase() + role.slice(1)
}

const closeModal = function (): void {
  visibleRef.value = false
  existingUsers.value = []
  selectedUsers.value = []
  messageToNewContributors.value = ''
  confirmTransferOpen.value = false
  confirmTransferTargetUuid.value = ""
  confirmTransferTargetName.value = ""
  roleChangeError.value = false
}

const handleRemoveModalClosed = () => {
  fetchCollectionUsers()
  removeContributorModalOpen.value = false
  selectedUserUuid.value = ''
  selectedUserName.value = ''
}

const fetchCollectionUsers = async function() {
  if (!currentFolder.value?.uuid) return
  loadingExistingUsers.value = true
  errorExistingUsers.value = false
  try {
    const users = await getCollectionUsers(currentFolder.value.uuid)
    if (users && typeof users === 'object' && 'users' in users) {
      existingUsers.value = users.users as {user_uuid: string, role: string, user_name: string, user_email: string}[]
    } else {
      throw new Error('Invalid API response format')
    }
  } catch (e) {
    console.error('Failed to fetch collection users:', e)
    errorExistingUsers.value = true
  }
  loadingExistingUsers.value = false
}

const { getReactiveEventAttributes } = useLibraryFolderAnalytics()
const analytics = getAnalyticsTracker().getChildContext(getReactiveEventAttributes())

const addContributors = async function() {
  if (selectedUsers.value.length === 0) {
    return
  }

  updatingFolder.value = true
  try {
    const selectedUserUuids = selectedUsers.value.map(user => user.UserUUID)
    const message = messageToNewContributors.value
    await grantContributors(currentFolder.value.uuid, selectedUserUuids, message)
    updateError.value = false
    fetchCollectionUsers()

    analytics.sendEvent(EVENT_NAMES.LIBRARY.FOLDER_ACTION.CLICKED_SEND_SHARED_FOLDER_INVITATION, {
      customized_invite_message: !!message,
      invited_user_uuids: selectedUserUuids
    })

    goBackToUserSelection()
    toastMessage.value = 'Share settings updated'
    showToast.value = true

    setTimeout(() => {
      showToast.value = false
    }, 3000)
  } catch (e) {
    updateError.value = true
    console.error('Failed to add contributors:', e)
  }
  updatingFolder.value = false
}

const removeContributorModalOpen = ref(false)
const selectedUserUuid = ref('')
const selectedUserName = ref('')

const removeContributors = async function(userUuid: string, name: string) {
  selectedUserUuid.value = userUuid
  selectedUserName.value = name
  removeContributorModalOpen.value = true
}

const handleTransferOwnership = async (userUuid: string) => {
  if (!currentFolder.value?.uuid) return
  updatingFolder.value = true
  try {
    await transferOwnership(currentFolder.value.uuid, userUuid)
    analytics.sendEvent(EVENT_NAMES.LIBRARY.FOLDER_ACTION.TRANSFERRED_FOLDER_OWNERSHIP, 
      {
        folder_uuid: currentFolder.value.uuid,
        folder_owner: userId.value,
        transferred_user_uuid: userUuid
      }
    )
    updateError.value = false
    await fetchCollectionUsers()
    toastError.value = false
    toastMessage.value = 'Ownership transferred'
    showToast.value = true
    setTimeout(() => { showToast.value = false }, 3000)
  } catch (e) {
      console.error('Failed to transfer ownership:', e)
      updateError.value = true
      toastError.value = true
      toastMessage.value = 'Failed to transfer ownership'
      showToast.value = true
      setTimeout(() => { showToast.value = false; toastError.value = false }, 3000)
  }
  updatingFolder.value = false
}

const handleUserSelected = (user: ContributorUserInfo) => {
  if (!selectedUsers.value.some(selected => selected.UserUUID === user.UserUUID)) {
    selectedUsers.value.push(user)
  }
}
const removeSelectedUser = (userUuid: string) => {
  selectedUsers.value = selectedUsers.value.filter(user => user.UserUUID !== userUuid)
  if (selectedUsers.value.length === 0) {
    messageToNewContributors.value = ''
    updateError.value = false
  }
}

const goBackToUserSelection = () => {
  selectedUsers.value = []
  messageToNewContributors.value = ''
  updateError.value = false
}
function sendContributorTooltipEvent() {
  analytics.sendEvent(EVENT_NAMES.LIBRARY.REVEALED_CONTRIBUTOR_TOOLTIP)
}

const getUserMenuItems = (user: { user_uuid: string; user_name: string }): MenuItemType[] => [
  {
    label: 'Transfer Ownership',
    action: () => openConfirmTransfer(user.user_uuid, user.user_name)
  },
  {
    label: 'Remove access',
    action: () => removeContributors(user.user_uuid, user.user_name)
  }
]

const openViewOnlyModal = () => {
  emit('open-share')
}


</script>

<template>
  <GbDialog
    dialogId="roles-and-permissions-modal"
    v-model:visible="visibleRef"
    :footer-buttons="true"
    :footer-classes="showViewOnlyLinkButton && selectedUsers.length === 0 ? 'justify-between' : ''"
    :show-header-content="true"
  >
    <template #headerContent>
      <div class="flex items-center justify-between px-2">
        <div class="flex items-center">
          <button
            type="button"
            @click="goBackToUserSelection"
            v-if="selectedUsers.length > 0"
            class="material-icons-round text-gray-700 hover:bg-gray-200 rounded-full p-1 mr-2 cursor-pointer"
            aria-label="Back to user selection"
          >
            arrow_back
          </button>
          <span class="text-xl leading-7" id="roles-and-permissions-modal_header">Share </span>
          <span class="material-icons-round ml-2 mr-1 text-display-sm text-gray-700">folder</span>
          <span class="font-bold fs-exclude">{{currentFolder.name}}</span>
        </div>
      </div>
    </template>
    <template #bodyContent>
      <div class="flex flex-col gap-2 px-2">
        <div class="flex items-center justify-between">

          <h3 v-if="canAddContributors" class="flex items-center font-bold">Add Contributors<span class="material-icons-round ml-1 text-xl cursor-pointer"
            v-tooltip.bottom="{
              value: 'Contributor must be a part of your organization and have an active Goalbook Toolkit license',
              class: 'tooltip-style tooltip-style-z'
            }">info_outline</span></h3>
          <h3 v-else class="flex items-center font-bold">Folder access</h3>
          <span class="text-sm text-blue-400 underline decoration-dotted cursor-pointer"
            v-tooltip.bottom="{
              value: 'Contributors may add content to this folder but may not edit or remove content once it has been added.',
              class: 'tooltip-style tooltip-style-z'
              }"
            @mouseenter="sendContributorTooltipEvent"
          >
            What can a Contributor do?
          </span>
        </div>

        <div class="mb-8">
          <template v-if="canAddContributors">
            <UserAutoComplete
              id="user-search"
              :selected-users-external="selectedUsers"
              :domain-uuid="userStore.userDomainUuid"
              @user-selected="handleUserSelected"
              @user-removed="removeSelectedUser"
            />
          </template>
          <template v-else>
            <div class="w-full rounded-sm border border-gray-700 bg-gray-100 text-gray-800 py-3 px-4">
              Only Owners can add Contributors
            </div>
          </template>
        </div>

        <GbInlineMessage gbTheme="red" v-if="updateError">
          We couldn't update the folder contributors. Please try again later.
        </GbInlineMessage>
        <div v-if="selectedUsers.length > 0" class="mt-2">
          <textarea
            v-model="messageToNewContributors"
            class="w-full placeholder-gray-700 py-2 px-3 border border-gray-700 rounded-sm fs-exclude"
            placeholder="Message (optional)"
            rows="5"
            maxlength="999"
          />
          <GbInlineMessage gbTheme="red" v-if="messageToNewContributors.length > 999">
            Message must be 999 characters or less.
          </GbInlineMessage>
        </div>
        <div v-else>
          <h3 class="font-bold mb-2">Folder members</h3>
          <div class="flex flex-col max-h-[280px] overflow-y-auto">
            <div v-if="errorExistingUsers || roleChangeError">
              <GbInlineMessage gbTheme="red"><span class="flex items-center"><span class="material-icons-round text-red-300">warning_amber</span><span class="ml-2">We're not able to retrieve folder members. Please try again later.</span></span></GbInlineMessage>
            </div>
            <div v-else-if="loadingExistingUsers" class="p-4 flex flex-col items-center justify-center">
              <ProgressSpinner class="h-8 w-8 stroke-primary-400" strokeWidth="8"/>
              <p class="text-center grow p-2">Loading...</p>
            </div>
            <template v-else>
              <div v-for="user in existingUsers" :key="user.user_uuid" class="flex items-center justify-between border-l-2 border-gray-400 border-solid p-2 folder-member">
                <span class="font-medium fs-exclude">{{user.user_name}}<br/><span class="text-sm font-regular fs-exclude">{{user.user_email}}</span></span>
                <div v-if="canAddContributors" class="relative flex items-center">
                  <div v-if="user.role !== 'owner'" class="relative flex items-center gap-1">
                    <span class="text-md font-medium">{{ displayRole(user.role) }}</span>
                    <PopupMenu
                      :items="getUserMenuItems(user)"
                      triggerIcon="arrow_drop_down"
                      :triggerAriaLabel="`Actions for ${user.user_name}`"
                      align="end"
                      containerPortal="#roles-and-permissions-modal"
                    />
                  </div>
                  <span v-else class="text-md font-medium mr-2">{{ displayRole(user.role) }}</span>
                </div>

                <div v-else class="flex items-center"><span class="text-md font-medium">{{ displayRole(user.role) }} </span>
                  <button
                    class="remove-collaborator material-icons-round text-lg ml-2 cursor-pointer hover:bg-gray-300 rounded-full p-1 fs-exclude"
                    v-if="user.role !== 'owner' && user.user_uuid === userId"
                    @click="removeContributors(user.user_uuid, user.user_name)"
                    :aria-label="`Remove ${user.user_name} as contributor`"
                    type="button">
                    close
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template #footerButtonRight>
      <GbButton
        type="button"
        :gbTheme="selectedUsers.length > 0 ? 'blueTertiary' : 'bluePrimary'"
        :label="selectedUsers.length > 0 ? 'Cancel' : 'Done'"
        @click="closeModal"
      />
      <GbButton
        type="button"
        gbTheme="bluePrimary"
        label="Send"
        @click="addContributors"
        v-if="selectedUsers.length > 0"
      />
    </template>

    <template #footerButtonLeft>
      <GbButton
        type="button"
        gbTheme="blueOutlineSecondary"
        v-if="showViewOnlyLinkButton && selectedUsers.length === 0"
        @click="openViewOnlyModal">
        <span class="material-icons-outlined leading-[0] pr-2">link</span>
        <span class="text-md">View-only Link</span>
      </GbButton>
    </template>
  </GbDialog>
  <!-- Confirm Transfer Ownership Modal -->
  <GbDialog
    dialogId="confirm-transfer-ownership"
    v-model:visible="confirmTransferOpen"
    :footer-buttons="true"
    :show-header-content="true"
  >
    <template #headerContent>
      <div class="flex items-center justify-between px-2">
        <h3 id="confirm-transfer-ownership_header" class="text-xl font-semibold">
          Transfer ownership to <span class="font-bold fs-exclude">{{ confirmTransferTargetName }}</span>
        </h3>
      </div>
    </template>
    <template #bodyContent>
      <div class="px-2 py-2 text-default">
        <p>
          By transferring ownership, you remove yourself as owner and become a contributor. You will no longer be able to edit content in this folder or add and remove other contributors. The new owner will be notified and could remove you.
        </p>
      </div>
    </template>
    <template #footerButtonRight>
      <GbButton type="button" gbTheme="blueTertiary" label="Cancel" @click="cancelConfirmTransfer"></GbButton>
      <GbButton
        type="button"
        gbTheme="bluePrimary"
        label="Transfer Ownership"
        :disabled="confirmTransferLoading || updatingFolder"
        @click="confirmTransfer"
      ></GbButton>
    </template>
  </GbDialog>
  <ToastProvider>
    <ToastRoot
      :open="showToast"
      @update:open="showToast = $event"
      class="rounded-md shadow-sm py-2 px-4 grid grid-cols-[auto_max-content] items-center data-[state=open]:animate-slideIn data-[state=closed]:animate-hide fixed top-16 left-1/2 transform -translate-x-1/2 z-50"
      :class="toastError ? 'bg-red-100 text-default border-red-300 border-1' : 'bg-black text-white'"
    >
      <div class="flex items-center">
        <span class="material-icons-outlined text-display-sm" v-if="!toastError">group</span>
        <span class="material-icons-round text-display-sm text-red-300" v-else>warning_amber</span>
        <span class="ml-2">{{ toastMessage }}</span>
      </div>
    </ToastRoot>
    <ToastViewport :label="`Notification - roles and permissions`" class="fixed top-0 left-0 right-0 flex justify-center p-[25px] gap-[10px] w-full max-w-[100vw] m-0 list-none z-100 outline-none" />
  </ToastProvider>
  <RemoveContributorModal
    v-model:externalOpen="removeContributorModalOpen"
    :userUuid="selectedUserUuid"
    :userName="selectedUserName"
    @modal-closed="handleRemoveModalClosed"
    @removed-self="closeModal"
  />
</template>
